import { useMemo } from "react";
import { AccountType } from "shared/types/accounts";
import AccountAvatar from "components/AccountAvatar/AccountAvatar";
import { getPlatformNameType } from "utils/accounts";
import { isPausedAccount } from "@/entities/accounts/libs/get-account-statuses";
import {
  doesAccountNeedApiReconnect,
  isAccountPostingAndInsightsEnabled,
} from "@/entities/accounts";
import { TooltipWrapper } from "ui/TooltipWrapper";
import { getAccountLabel } from "../account-label";

interface AccountOptionProps {
  account: AccountType;
  className?: string;
}

/**
 * Reusable account row component
 * Shows account avatar with status indicators and account name
 */
export function AccountOption({ account, className }: AccountOptionProps) {
  const name = getAccountLabel(account);
  const { platformType, platformName } = getPlatformNameType(account);

  // Check if account is fully connected and functional
  const isFullyConnected =
    !doesAccountNeedApiReconnect(account) &&
    isAccountPostingAndInsightsEnabled(account);

  const disconnected = useMemo(() => {
    return (
      isPausedAccount(account) ||
      account?.status === "failed" ||
      account?.promptApiReconnect
    );
  }, [account]);

  return (
    <div className={`flex items-center w-full min-w-0 ${className || ""}`}>
      <AccountAvatar
        profilePictureUrl={account.profilePictureUrl}
        platformType={platformType}
        color={account.color}
        size={"small"}
        placement={"top"}
        tooltip={platformName}
        disconnected={disconnected || !isFullyConnected}
        useYtRedPlayIcon
        className="mr-2 flex-shrink-0"
      />
      <TooltipWrapper tooltip={name} className="min-w-0">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
          {name}
        </span>
      </TooltipWrapper>
    </div>
  );
}
